import { gql, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import USER_AVATAR_FRAGMENT from '../graphql/fragments/UserAvatar';
import type {
  PostOwnerContributorsQueryQuery,
  PostOwnerContributorsQueryQueryVariables,
} from '../graphql/generated/graphqlTypes';
import UserRow from '../user/UserRow';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import styles from './PostContributors.module.scss';
import PostOwnerSelect from './PostOwnerSelect';
import { PostPolicyAction, usePostPolicyCheck } from './postPolicyCheck';

const POST_CONTRIBUTOR_FRAGMENT = gql`
  fragment PostContributor on User {
    id
    name
    title
    type
    ...UserAvatar
  }
  ${USER_AVATAR_FRAGMENT}
`;

const POST_OWNER_CONTRIBUTORS_QUERY = gql`
  query postOwnerContributorsQuery($id: ID!) {
    post(id: $id) {
      id
      ...PostOwnerSelect_post
      ...usePostPolicyCheck_post
      owner {
        ...PostContributor
      }
      contributors {
        ...PostContributor
      }
    }
  }
  ${PostOwnerSelect.fragments.post}
  ${usePostPolicyCheck.fragments.post}
  ${POST_CONTRIBUTOR_FRAGMENT}
`;

interface PostContributorsProps {
  postId: string;
}

const PostContributors = ({ postId }: PostContributorsProps) => {
  const { data } = useQuery<
    PostOwnerContributorsQueryQuery,
    PostOwnerContributorsQueryQueryVariables
  >(POST_OWNER_CONTRIBUTORS_QUERY, {
    variables: { id: postId },
  });
  const post = data?.post;
  const user = useAppSelector((state) => state.session.user);
  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [isShowingAll, setIsShowingAll] = useState(false);

  const postPolicyCheck = usePostPolicyCheck();

  if (!post) return null;

  const { owner } = post;
  const contributors = post.contributors.filter((c) => c.id !== owner.id);

  const visibleContributors = contributors.slice(
    0,
    isShowingAll ? undefined : 4,
  );

  return (
    <div className={styles.container}>
      <div className={styles.ownerContainer} role="group">
        <div className={styles.title} role="heading" aria-level={5}>
          Owner
        </div>
        {isEditingOwner ? (
          <ClickOutsideHandler onClickOutside={() => setIsEditingOwner(false)}>
            <PostOwnerSelect
              menuPlacement="top"
              owner={owner}
              onChange={() => setIsEditingOwner(false)}
              post={post}
            />
          </ClickOutsideHandler>
        ) : (
          <>
            <div className={styles.ownerRow}>
              <UserRow link showHoverCard user={owner} />
            </div>
            {postPolicyCheck(PostPolicyAction.EditPost, post, user) && (
              <div className={styles.editButton}>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setIsEditingOwner(!isEditingOwner)}
                >
                  Edit
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      {visibleContributors.length > 0 && (
        <div className={styles.contributorsList} role="group">
          <div className={styles.title} role="heading" aria-level={5}>
            Contributors
          </div>
          <div
            className={styles.contributorList}
            role="list"
            aria-label="Contributors"
          >
            {visibleContributors.map((contributor) => (
              <UserRow
                key={contributor.id ?? undefined}
                link
                showHoverCard
                user={contributor}
              />
            ))}
            {!isShowingAll &&
              contributors.length > visibleContributors.length && (
                <Button variant="link" onClick={() => setIsShowingAll(true)}>
                  Show {contributors.length - visibleContributors.length} more
                  <div className={styles.caretWrapper}>
                    <Caret direction="down" />
                  </div>
                </Button>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PostContributors;
